import { cardTypeRequiresCvv } from './card-cvv-policy.js';

const messages = {
    card: {
        amexCardOrCvv: 'Invalid Amex card or CVV.',
        cardOrCvv: 'Invalid card or CVV.',
        cvvLength: function (length) {
            return 'The CVV value must be ' + length + ' characters long';
        },
        enterNumber: 'Enter the card number.',
        expiryMonth: 'Invalid Month - must be between 1 and 12',
        expiryMonthShort: 'Invalid month (1-12).',
        expiryYear: 'Invalid Year Entered',
        expiryYearShort: 'Invalid year.',
        expiryYearTooFar: function (maxYear) {
            return 'The expiry year must not be greater than ' + maxYear + '.';
        },
        expired: 'Card has expired',
        name: 'Enter the name on the card.',
        numberLength: function (length) {
            return 'Invalid Card - it should be ' + length + ' digits';
        },
        dafNumberTooShort:
            'This card number is too short. OJC, Pledger, and Donors Fund numbers must be at least 14 digits.',
        dafNumberTooLong:
            'This card number is too long. OJC, Pledger, and Donors Fund numbers must be at most 16 digits.',
    },
    campaignLink: 'Use only letters, numbers, underscores, and hyphens',
    email: 'Please enter a valid email address',
    phone: 'Please enter phone number in the following format: 999-999-9999',
    pin: {
        numbersOnly: 'Your PIN must contain only numbers.',
    },
    postalCode: 'Please enter a valid US or Canadian postal code',
    addFly: {
        guestFields: 'Please select both relationship type and host member for guests.',
        memberContact: 'Please provide either a phone number or email.',
        memberName: 'Please fill in first and last name.',
    },
};

const patterns = {
    cardExpiryMonth: '(0?[1-9]|1[0-2])',
    cardExpiryYear: '[0-9]{2}',
    cardCvv: '[0-9]{3,4}',
    campaignLink: '[A-Za-z0-9_\\-]+',
    ipAddress: '((\\d|[1-9]\\d|1\\d\\d|2[0-4]\\d|25[0-5])\\.){3}(\\d|[1-9]\\d|1\\d\\d|2[0-4]\\d|25[0-5])',
    phone: '[0-9]{3}-[0-9]{3}-[0-9]{4}',
    postalCode: '((\\d{5}-\\d{4})|(\\d{5})|([A-Za-z]\\d[A-Za-z][ ]?\\d[A-Za-z]\\d))',
};

const masks = {
    cardCvv: '999[9]',
    cardExpiryMonth: '9[9]',
    cardExpiryYear: '99',
    phone: '999-999-9999',
    postalCanada: 'A9A 9A9',
    postalUs: '99999',
};

const cardMaskSelectors = {
    cvv: '#cvv2, [name="cvv2"]',
    expiryMonth: '#expiry_month, [name="expiry_month"]',
    expiryYear: '#expiry_year, [name="expiry_year"]',
};

const cardValidationSelectors = Object.assign({
    card: '#card, [name="card"]',
    postalCode: '#avs_zip, [name="avs_zip"]',
}, cardMaskSelectors);

function toJquery(target) {
    return target && target.jquery ? target : $(target);
}

function customValidityMessage(message, element) {
    return typeof message === 'function' ? message(element) : message;
}

function bindCustomValidity(target, message, namespace) {
    const suffix = namespace || '.fieldValidation';
    const $fields = toJquery(target);

    $fields
        .off('invalid' + suffix + ' input' + suffix + ' change' + suffix)
        .on('invalid' + suffix, function () {
            this.setCustomValidity(customValidityMessage(message, this));
        })
        .on('input' + suffix + ' change' + suffix, function () {
            this.setCustomValidity('');
        });
}

function bindDelegatedCustomValidity(root, selector, message, namespace) {
    const suffix = namespace || '.fieldValidation';
    const rootElement = toJquery(root)[0];

    if (!rootElement) {
        return;
    }

    rootElement.fieldValidationHandlers = rootElement.fieldValidationHandlers || {};
    const handlerKey = suffix + ':' + selector;

    if (rootElement.fieldValidationHandlers[handlerKey]) {
        rootElement.removeEventListener('invalid', rootElement.fieldValidationHandlers[handlerKey], true);
    }

    rootElement.fieldValidationHandlers[handlerKey] = function (event) {
        if (event.target.matches(selector)) {
            event.target.setCustomValidity(customValidityMessage(message, event.target));
        }
    };
    rootElement.addEventListener('invalid', rootElement.fieldValidationHandlers[handlerKey], true);

    toJquery(root)
        .off('input' + suffix + ' change' + suffix, selector)
        .on('input' + suffix + ' change' + suffix, selector, function () {
            this.setCustomValidity('');
        });
}

function clearValidity(target) {
    toJquery(target).each(function () {
        this.setCustomValidity('');
    });
}

function isMasked(element) {
    return !!(element.inputmask || $(element).data('inputmask'));
}

function applyPhoneConstraints(target, enabled) {
    toJquery(target).each(function () {
        const $field = $(this);

        if (enabled) {
            $field.attr('pattern', patterns.phone);
            $field.attr('minlength', '12');
            if (isMasked(this)) {
                $field.removeAttr('maxlength');
            } else {
                $field.attr('maxlength', '12');
            }
            return;
        }

        $field.removeAttr('pattern');
        $field.removeAttr('minlength');
    });
}

function applyPhoneMask(target) {
    const $scope = toJquery(target || document);
    const $fields = $scope.is(AUTO_PHONE_SELECTOR) ? $scope : $scope.find(AUTO_PHONE_SELECTOR);

    if (typeof $.fn.inputmask !== 'function' || !$fields.length) {
        return;
    }

    $fields.inputmask(masks.phone).removeAttr('maxlength');
}

function applyPostalCodeMask(target, countryCode) {
    const $fields = toJquery(target);

    if (typeof $.fn.inputmask !== 'function') {
        return;
    }

    $fields.inputmask(countryCode === 'USA' ? masks.postalUs : masks.postalCanada);
}

function maskedFields(root, selector) {
    const $root = toJquery(root);

    return $root.is(selector) ? $root : $root.find(selector);
}

function absorbBrowserAutofill(target) {
    toJquery(target).each(function () {
        const mask = this.inputmask;
        if (!mask || typeof mask._valueGet !== 'function') {
            return;
        }
        const native = String(mask._valueGet() || '').replace(/_/g, '').trim();
        if (native === '') {
            return;
        }
        const shown = String($(this).val() || '').replace(/_/g, '').trim();
        if (shown === native) {
            return;
        }
        $(this).trigger('setvalue');
    });
}

function applyInputMask(target, mask) {
    toJquery(target).each(function () {
        const $field = $(this);

        if (!$field.data('inputmask')) {
            $field.inputmask(mask);
        }
        $field.removeAttr('maxlength');
        absorbBrowserAutofill(this);
    });
}

function applyCardMasks(root, selectors) {
    if (typeof $.fn.inputmask !== 'function') {
        return;
    }

    const fields = $.extend({}, cardMaskSelectors, selectors || {});

    applyInputMask(maskedFields(root, fields.cvv), masks.cardCvv);
    applyInputMask(maskedFields(root, fields.expiryMonth), masks.cardExpiryMonth);
    applyInputMask(maskedFields(root, fields.expiryYear), masks.cardExpiryYear);
}

function validateCardPaymentFields(root, selectors, options) {
    const fields = $.extend({}, cardValidationSelectors, selectors || {});
    const settings = $.extend({ requireCreditFields: true }, options || {});
    const $card = maskedFields(root, fields.card).first();
    const $cvv = maskedFields(root, fields.cvv).first();
    const $expiryMonth = maskedFields(root, fields.expiryMonth).first();
    const $expiryYear = maskedFields(root, fields.expiryYear).first();
    const $postalCode = fields.postalCode ? maskedFields(root, fields.postalCode).first() : $();

    absorbBrowserAutofill($card);
    absorbBrowserAutofill($cvv);
    absorbBrowserAutofill($expiryMonth);
    absorbBrowserAutofill($expiryYear);
    absorbBrowserAutofill($postalCode);
    clearValidity($().add($card).add($cvv).add($expiryMonth).add($expiryYear).add($postalCode));

    const expiry = cardExpiryState($expiryMonth.val(), $expiryYear.val());
    if (!expiry.hasValidMonth) {
        return reportFieldError($expiryMonth, messages.card.expiryMonth);
    }
    if (!expiry.hasValidYear || expiry.year < expiry.currentYear) {
        return reportFieldError($expiryYear, messages.card.expiryYear);
    }
    if (expiry.isBeyondMax) {
        return reportFieldError($expiryYear, messages.card.expiryYearTooFar(expiry.maxYear));
    }
    if (expiry.isExpired) {
        return reportFieldError($expiryYear, messages.card.expired);
    }

    if (!settings.requireCreditFields) {
        if (dafPanDigitCount($card.val()) < DAF_MIN_PAN_DIGITS) {
            return reportFieldError($card, messages.card.dafNumberTooShort);
        }
        if (dafPanDigitCount($card.val()) > DAF_MAX_PAN_DIGITS) {
            return reportFieldError($card, messages.card.dafNumberTooLong);
        }
        return true;
    }

    const card = $card.val();
    const expectedCardLength = expectedCardNumberLength(card);
    const expectedCvvLength = expectedCardCvvLength(card);

    if (!hasExpectedCardNumberLength(card)) {
        return reportFieldError($card, messages.card.numberLength(expectedCardLength));
    }
    if (!hasExpectedCardCvvLength(card, $cvv.val())) {
        return reportFieldError($cvv, messages.card.cvvLength(expectedCvvLength));
    }
    if ($postalCode.length && !hasPostalCodeFormat($postalCode.val())) {
        return reportFieldError($postalCode, messages.postalCode);
    }

    return true;
}

function applyAddressPostalControls(zipTarget, zipLabelTarget, stateLabelTarget, countryCode) {
    const isUsa = countryCode === 'USA';
    const $zip = toJquery(zipTarget);

    $zip
        .attr('maxlength', isUsa ? '5' : '7')
        .attr('inputmode', isUsa ? 'numeric' : 'none');

    applyPostalCodeMask($zip, countryCode);
    toJquery(zipLabelTarget).text(isUsa ? 'Zip Code' : 'Postal Code');
    toJquery(stateLabelTarget).text(isUsa ? 'State' : 'Province/Territory');
}

function syncCvvFieldForCardType(cvvTarget, cardType) {
    const $cvv = toJquery(cvvTarget);
    if (!$cvv.length) {
        return;
    }
    const required = cardTypeRequiresCvv(cardType);
    $cvv.prop('required', required);
    if (required) {
        $cvv.attr('pattern', patterns.cardCvv);
    } else {
        $cvv.removeAttr('pattern');
        clearValidity($cvv);
    }
}

function applyCardConstraints(root) {
    const $root = toJquery(root);
    $root.find('#expiry_month, [name="expiry_month"]').attr('pattern', patterns.cardExpiryMonth).attr('maxlength', '2');
    $root.find('#expiry_year, [name="expiry_year"]').attr('pattern', patterns.cardExpiryYear).attr('maxlength', '2');
    $root.find('#cvv2, [name="cvv2"]').each(function () {
        if (this.required) {
            $(this).attr('pattern', patterns.cardCvv);
            return;
        }
        $(this).removeAttr('pattern');
    });
}

function applyCampaignLinkConstraints(root) {
    const $root = toJquery(root);
    const selector = 'input[name="link_for_campaign[]"], .campaign-link-input';
    const $fields = $root.is(selector) ? $root : $root.find(selector);

    if (!$fields.length) {
        return;
    }

    $fields.attr('pattern', patterns.campaignLink).attr('maxlength', '50');
    bindCustomValidity($fields, messages.campaignLink, '.campaignLinkValidation');
}

function applyIpAddressConstraints(root) {
    const $root = toJquery(root);
    const selector = '#lock_ip_address, [name="lock_ip_address"]';
    const $fields = $root.is(selector) ? $root : $root.find(selector);

    $fields.attr('pattern', patterns.ipAddress);
}

function normalizedPhone(value) {
    return String(value || '').trim().replace(/_/g, '');
}

function hasPhoneFormat(value) {
    return new RegExp('^' + patterns.phone + '$').test(normalizedPhone(value));
}

function formatPhoneForSubmit(value) {
    const digits = String(value || '').replace(/\D/g, '');
    if (digits.length === 10) {
        return digits.slice(0, 3) + '-' + digits.slice(3, 6) + '-' + digits.slice(6);
    }
    if (digits.length === 11 && digits.charAt(0) === '1') {
        return digits.slice(1, 4) + '-' + digits.slice(4, 7) + '-' + digits.slice(7);
    }
    return normalizedPhone(value);
}

function hasEmailFormat(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
}

function hasPostalCodeFormat(value) {
    return new RegExp('^' + patterns.postalCode + '$').test(String(value || '').trim());
}

function validatePostalCodeField(target) {
    clearValidity(target);

    if (!hasPostalCodeFormat(toJquery(target).val())) {
        return reportFieldError(target, messages.postalCode);
    }

    return true;
}

function normalizedCardNumber(value) {
    return String(value || '').replace(/\s/g, '');
}

function normalizedMaskedValue(value) {
    return String(value || '').replace(/_/g, '').trim();
}

function isAmexCardNumber(value) {
    return /^3[47]/.test(normalizedCardNumber(value));
}

function expectedCardNumberLength(value) {
    return isAmexCardNumber(value) ? 15 : 16;
}

function expectedCardCvvLength(cardValue) {
    return isAmexCardNumber(cardValue) ? 4 : 3;
}

function hasExpectedCardNumberLength(value) {
    const digits = normalizedCardNumber(value);

    return digits.length === expectedCardNumberLength(digits);
}

function hasExpectedCardCvvLength(cardValue, cvvValue) {
    return normalizedMaskedValue(cvvValue).length === expectedCardCvvLength(cardValue);
}

const CARD_EXPIRY_MAX_YEARS_AHEAD = 20;

function fullExpiryYear(value) {
    const raw = normalizedMaskedValue(value);
    if (/^\d{2}$/.test(raw)) {
        return '20' + raw;
    }
    return raw;
}

function cardExpiryState(monthValue, yearValue) {
    const monthRaw = normalizedMaskedValue(monthValue);
    const yearRaw = normalizedMaskedValue(yearValue);
    const fullYearRaw = fullExpiryYear(yearRaw);
    const month = parseInt(monthRaw, 10);
    const year = parseInt(fullYearRaw, 10);
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    const maxYear = currentYear + CARD_EXPIRY_MAX_YEARS_AHEAD;
    const hasValidMonth = /^\d{1,2}$/.test(monthRaw) && Number.isFinite(month) && month >= 1 && month <= 12;
    const hasValidYear = /^\d{4}$/.test(fullYearRaw) && Number.isFinite(year);

    return {
        currentMonth: currentMonth,
        currentYear: currentYear,
        maxYear: maxYear,
        hasValidMonth: hasValidMonth,
        hasValidYear: hasValidYear,
        isBeyondMax: hasValidYear && year > maxYear,
        isExpired: hasValidYear && (year < currentYear || (year === currentYear && month < currentMonth)),
        month: month,
        year: year,
    };
}

function reportIfInvalid(target) {
    const field = toJquery(target)[0];

    if (!field || field.checkValidity()) {
        return false;
    }

    field.reportValidity();
    return true;
}

function fieldErrorHtml(message) {
    return '<i class="fa-regular fa-circle-x"></i> ' + message;
}

function reportFieldError(target, message) {
    const $field = toJquery(target);
    const field = $field[0];

    if (!field) {
        return false;
    }

    field.setCustomValidity(message);
    field.focus();
    field.reportValidity();
    $field
        .off('input.reportFieldError change.reportFieldError')
        .one('input.reportFieldError change.reportFieldError', function () {
            this.setCustomValidity('');
        });
    return false;
}

function requireValue(target, message) {
    const field = toJquery(target)[0];

    if (!field) {
        return true;
    }

    if (String(field.value || '').length > 0) {
        field.setCustomValidity('');
        return true;
    }

    field.setCustomValidity(message);
    field.reportValidity();
    return false;
}

function requireChoice(target, message) {
    const field = toJquery(target)[0];

    if (!field) {
        return true;
    }

    if (field.value !== null && field.value !== '') {
        field.setCustomValidity('');
        return true;
    }

    field.setCustomValidity(message);
    field.reportValidity();
    return false;
}

function passwordError(password, confirmation, rules) {
    if (password !== confirmation) {
        return 'Your password and confirmation password do not match';
    }

    if (password.length < rules.min) {
        return 'Your password must be at least ' + rules.min + ' characters';
    }

    if (rules.mix && !/(?=.*[a-z])(?=.*[A-Z])/.test(password)) {
        return 'Your password must contain at least one uppercase and lowercase letters';
    }

    if (rules.number && !/\d/.test(password)) {
        return 'Your password must contain at least one number';
    }

    if (rules.symbol && !/(?=.*[-+_!@#$%^&*., ?])/.test(password)) {
        return 'Your password must contain at least one special character';
    }

    return '';
}

function isAllowedPinKey(key, value) {
    if (key.length !== 1) {
        return true;
    }
    if ('0123456789'.indexOf(key) > -1) {
        return true;
    }
    return key === '.' && String(value || '').indexOf('.') === -1;
}

function pinError(pin, confirmation, rules) {
    if (pin !== confirmation) {
        return 'Your PIN and confirmation PIN do not match';
    }

    if (pin.length < rules.min || pin.length > rules.max) {
        return 'Your PIN must be between ' + rules.min + ' AND ' + rules.max + ' numbers';
    }

    return '';
}

function validatePinFields(pinTarget, confirmationTarget, rules) {
    const $pin = toJquery(pinTarget);
    const $confirmation = toJquery(confirmationTarget);
    const pin = String($pin.val() || '');
    const confirmation = String($confirmation.val() || '');

    clearValidity($().add($pin).add($confirmation));

    if (!/^\d+$/.test(pin)) {
        return reportFieldError($pin, messages.pin.numbersOnly);
    }
    if (!/^\d+$/.test(confirmation)) {
        return reportFieldError($confirmation, messages.pin.numbersOnly);
    }

    const message = pinError(pin, confirmation, rules);
    if (message) {
        return reportFieldError($confirmation, message);
    }

    return true;
}

const DAF_MIN_PAN_DIGITS = 14;
const DAF_MAX_PAN_DIGITS = 16;

function dafPanDigitCount(value) {
    return String(value || '').replace(/\D/g, '').length;
}

const AUTO_PHONE_SELECTOR = '.phone';
const AUTO_EMAIL_SELECTOR = 'input[type="email"]';

function autoApplyPhoneConstraints(root) {
    const $root = toJquery(root);
    const $fields = $root.is(AUTO_PHONE_SELECTOR) ? $root : $root.find(AUTO_PHONE_SELECTOR);

    if ($fields.length) {
        applyPhoneConstraints($fields, true);
    }
}

function initGlobalFieldValidation() {
    autoApplyPhoneConstraints(document);
    bindDelegatedCustomValidity(document, AUTO_PHONE_SELECTOR, messages.phone, '.globalPhoneValidation');
    bindDelegatedCustomValidity(document, AUTO_EMAIL_SELECTOR, messages.email, '.globalEmailValidation');

    if (typeof MutationObserver === 'undefined' || typeof document === 'undefined' || !document.body) {
        return;
    }

    new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
            $(mutation.addedNodes).each(function () {
                if (this.nodeType === 1) {
                    autoApplyPhoneConstraints(this);
                }
            });
        });
    }).observe(document.body, { childList: true, subtree: true });
}

function syncAddFlyUserTypeFromSegmentedControl(modalEl) {
    const $modal = toJquery(modalEl);
    const $activeInput = $modal.find('.segmented-control__option.active input[name="user_type"]');
    if (!$activeInput.length) {
        return null;
    }

    $modal.find('input[name="user_type"]').prop('checked', false);
    $activeInput.prop('checked', true);
    return $activeInput.val();
}

function resolveAddFlyUserType(modalEl) {
    const syncedType = syncAddFlyUserTypeFromSegmentedControl(modalEl);
    if (syncedType) {
        return syncedType;
    }

    const $checked = toJquery(modalEl).find('input[name="user_type"]:checked');
    return $checked.length ? $checked.val() : null;
}

function readAddFlySelectValue($modal, selector) {
    const $select = $modal.find(selector).first();
    if (!$select.length) {
        return '';
    }

    const wasDisabled = $select.prop('disabled');
    if (wasDisabled) {
        $select.prop('disabled', false);
    }

    const val = String($select.val() || '').trim();

    if (wasDisabled) {
        $select.prop('disabled', true);
    }

    return val;
}

function validateAddFlyContactFields(modalEl) {
    const $modal = toJquery(modalEl);
    syncAddFlyUserTypeFromSegmentedControl(modalEl);
    const userType = resolveAddFlyUserType(modalEl);
    const firstName = String($modal.find('#first_name').val() || '').trim();
    const lastName = String($modal.find('#last_name').val() || '').trim();
    const phone = normalizedPhone($modal.find('#phone').val());
    const email = String($modal.find('#email').val() || '').trim();
    const guestFieldsVisible = $modal.find('.add_fly_guests').is(':visible');
    const isGuestMode = guestFieldsVisible || userType === 'guest';

    if (isGuestMode) {
        const relationshipType = readAddFlySelectValue($modal, 'select[name="relationship_type_id"]');
        const hostUserId = readAddFlySelectValue($modal, 'select[name="host_user_id"]');
        if (!relationshipType || !hostUserId) {
            return messages.addFly.guestFields;
        }
    }

    if (!isGuestMode && userType === 'member' && (!firstName || !lastName)) {
        return messages.addFly.memberName;
    }

    if (!isGuestMode && userType === 'member' && !phone && !email) {
        return messages.addFly.memberContact;
    }

    if (phone && !hasPhoneFormat(phone)) {
        return messages.phone;
    }

    return null;
}

export {
    applyAddressPostalControls,
    applyCardConstraints,
    applyCardMasks,
    cardTypeRequiresCvv,
    syncCvvFieldForCardType,
    applyCampaignLinkConstraints,
    applyIpAddressConstraints,
    applyPhoneMask,
    applyPhoneConstraints,
    applyPostalCodeMask,
    bindCustomValidity,
    bindDelegatedCustomValidity,
    clearValidity,
    formatPhoneForSubmit,
    hasEmailFormat,
    hasPhoneFormat,
    hasPostalCodeFormat,
    cardExpiryState,
    expectedCardCvvLength,
    expectedCardNumberLength,
    fieldErrorHtml,
    fullExpiryYear,
    hasExpectedCardCvvLength,
    hasExpectedCardNumberLength,
    initGlobalFieldValidation,
    isAmexCardNumber,
    masks,
    messages,
    normalizedCardNumber,
    normalizedPhone,
    passwordError,
    pinError,
    isAllowedPinKey,
    patterns,
    reportFieldError,
    reportIfInvalid,
    requireChoice,
    requireValue,
    resolveAddFlyUserType,
    validateAddFlyContactFields,
    validateCardPaymentFields,
    validatePinFields,
    validatePostalCodeField,
};
